import type { NewsType } from '@dreamstack/feature-components/src/news/newsTypeUrls.static'
import { getNewsPath } from '@dreamstack/feature-components/src/news/getNewsPath'
import { BodyText } from '@dreamstack/feature-components/src/layout/Texts'
import { useLocale, useTranslation } from '@dreamstack/i18n'
import { ChevronDown } from '@dreamstack/icons/src/icon-components/ChevronDown'
import { ChevronUp } from '@dreamstack/icons/src/icon-components/ChevronUp'
import { trpc } from '@dreamstack/trpc/client'
import { cn } from '@dreamstack/util/src/cn'
import { Disclosure } from '@headlessui/react'
import map from 'lodash/map'
import Link from 'next/link'
import type { FunctionComponent, ReactNode, SVGProps } from 'react'
import { SuperRichTextWrapper } from '../SuperRichText'

const IconChevronDown = (props: SVGProps<SVGSVGElement>) => (
  <ChevronDown
    {...props}
    className={cn(
      'text-accentroAqua-full h-px-24 w-px-24 ml-px-8 md:h-px-32 md:w-px-32 max-w-px-96',
      props.className
    )}
  />
)
const IconChevronUp = (props: SVGProps<SVGSVGElement>) => (
  <ChevronUp
    {...props}
    className={cn(
      'text-accentroAqua-full h-px-24 w-px-24 ml-px-8 md:h-px-32 md:w-px-32',
      props.className
    )}
  />
)

const ButtonWrapper = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-row justify-start items-center text-accentroAqua-full font-semibold">
    {children}
  </div>
)

const OpacityElement = ({ isOpen }: { isOpen: boolean }) =>
  isOpen ? null : (
    <div
      className="z-10 absolute inset-x-0 bottom-[5px] h-[50px] sm:h-[55px] sm:bottom-[15px] md:h-[60px] md:bottom-[5px]"
      style={{
        backgroundImage:
          'linear-gradient(to bottom, rgba(255, 255, 255, 0) 55%, #fff)',
      }}
    />
  )

const ArticleLink = (
  props: React.AnchorHTMLAttributes<HTMLAnchorElement> & { children: ReactNode }
) => {
  const { children, className, ...rest } = props
  return (
    <a
      {...rest}
      className={cn(
        'cursor-pointer ml:text-px-20 text-accentroAqua-full font-semibold no-underline',
        className
      )}
    >
      {children}
    </a>
  )
}

const ArticleItem = ({ children }: { children: ReactNode }) => (
  <li className="news-article-item">{children}</li>
)

export type NewsArticleListCustomProps = {
  newsType: NewsType
}

export const NewsArticleList: FunctionComponent<
  React.PropsWithChildren<{
    customProps: NewsArticleListCustomProps
  }>
> = ({ customProps }) => {
  const newsType = customProps?.newsType
  const locale = useLocale()
  const t = useTranslation()

  const { data } = trpc.customBlockStuff.getAllNewsArticlesByType.useQuery(
    { locale, newsType: newsType ?? 'HomeAndLiving' },
    {
      trpc: { ssr: true },
      staleTime: Infinity,
      enabled: !!newsType,
    }
  )

  const articles = data?.items || []

  if (!newsType) return null

  return (
    <Disclosure>
      {({ open }) => (
        <div className="border-b-2 border-accentroGray-full pb-[70px] md:pb-[55px]">
          <Disclosure.Panel
            static
            className={cn(
              'w-full h-auto overflow-hidden relative mb-px-16',
              !open && 'max-h-[300px]'
            )}
          >
            <>
              <SuperRichTextWrapper>
                <ul className="list-disc marker:text-accentroAqua-full">
                  {map(articles, (article) => {
                    const slug = article?.slug ?? '#'
                    const href = getNewsPath({ locale, slug, newsType })
                    return (
                      <ArticleItem key={slug}>
                        <Link href={href} passHref legacyBehavior>
                          <ArticleLink target="_blank">
                            {article?.title}
                          </ArticleLink>
                        </Link>
                      </ArticleItem>
                    )
                  })}
                </ul>
              </SuperRichTextWrapper>
              <OpacityElement isOpen={open} />
            </>
          </Disclosure.Panel>
          <Disclosure.Button className="w-full focus:outline-none">
            <ButtonWrapper>
              {open ? (
                <>
                  <BodyText>{t('accentro:newsHub:showLess')}</BodyText>
                  <IconChevronUp />
                </>
              ) : (
                <>
                  <BodyText> {t('accentro:newsHub:showMore')}</BodyText>
                  <IconChevronDown />
                </>
              )}
            </ButtonWrapper>
          </Disclosure.Button>
        </div>
      )}
    </Disclosure>
  )
}
